import type { BrowseSeoMeta } from '@/redux/modules/browsePath/browsePath.reducer';

import {
    fetchResolveBrowseUrl,
    type ResolveBrowseUrlApiResponse,
    type ResolveBrowseUrlPayload,
} from '@/redux/api/browseUrl';
import {
    type BrowseDimensionPair,
    BROWSE_DIMENSION_PATH_TO_FACET_TYPE,
    browsePathHasTildeMultiValue,
    buildBrowseCategoryPath,
    mapBrowsePairsToSearchOptionIds,
    parseBrowseTail,
    sanitizeBrowseRedirectPath,
    sortBrowsePairsCanonical,
} from '@/utils/browsePath/browsePath';
import {
    getLegacyBrowseCanonicalRedirectPath,
    isLegacyFacetedNumericTailBrowsePath,
    isUnknownSlugOnlyBrowsePath,
    stripsLegacyNumericTailToOwnTopSlug,
} from '@/utils/browsePath/legacyQuadBrowseRedirect';
import { getTopCategoryIdFromSlug } from '@/utils/browsePath/resolveCategorySlug';

import { mapResolvedCategoryIdsToBrowseFacetOptions } from './queryBuilders';
import { CategoryRouteContext } from './routeContext';

type ResolveBrowseInputsArgs = {
    deployment: string;
    locationSearch: string;
    routeContext: CategoryRouteContext;
    searchFacets: any;
    stateAfterFacets: any;
};

export type BrowseResolutionResult = {
    baseCategoryIds: number[];
    browseFacetOptionIds: Record<string, string[]> | undefined;
    browsePathPairsForPostSearchRemap: BrowseDimensionPair[];
    redirectUrl: string | null;
    /**
     * Backend-authoritative SEO outputs (canonical/robots/indexability/label) from
     * `resolve-browse-url`. Threaded into the `browsePath` redux slice so the page
     * meta + H1 obey category-api instead of self-canonicalizing the raw pathname.
     */
    seo?: BrowseSeoMeta;
    shouldAttemptPostSearchBrowseRemap: boolean;
    /** Slug to keyword-search when no `baseCategoryIds` could be resolved (sub-category browse). */
    unresolvedBrowseSlugForKeywordSearch: string | null;
    /**
     * Pair values joined as a `searchTerm` for the initial search when we have a known top category
     * but couldn't resolve any facet ids. Narrows the initial result set so the post-search facet
     * aggregations contain the slug's option (e.g. `frank stella` → creator: Frank Stella appears in
     * the response, then `browsePathPairsForPostSearchRemap` resolves to its exact id and we re-dispatch
     * without the keyword). Necessary because category-api currently returns only `topCategorySlug` for
     * canonical `/c/{top}/{dimension}/{value}/` URLs (no `categoryIds`/`facetOptionsByFacetId`), and the
     * default unfiltered top-cat search's facet aggregations top out at the N most common values, which
     * excludes long-tail creators/materials/etc.
     */
    keywordFallbackForUnresolvedBrowse: string | null;
};

const hasAnyFacetIds = (facetOptionIds: Record<string, string[]> | undefined) =>
    Boolean(facetOptionIds && Object.values(facetOptionIds).some((ids) => Array.isArray(ids) && ids.length > 0));

/**
 * Maps a `resolve-browse-url` payload to the SEO slice shape. Returns `undefined`
 * when the API contributed no SEO signal, so callers can leave the slice empty
 * and fall back to search-party meta.
 */
const toSeoMeta = (payload: ResolveBrowseUrlPayload | undefined): BrowseSeoMeta | undefined => {
    if (!payload) {
        return undefined;
    }
    const { canonicalPath, label, robots, seoIndexable } = payload;
    if (
        canonicalPath === undefined &&
        robots === undefined &&
        seoIndexable === undefined &&
        (label === undefined || label === '')
    ) {
        return undefined;
    }
    return {
        canonicalPath: canonicalPath ?? null,
        label: label ?? null,
        robots: robots ?? null,
        seoIndexable: seoIndexable ?? null,
    };
};

/**
 * Joins the value slugs (not dimension keys) from canonical browse pairs into a single search keyword.
 * Used to narrow the initial search when we have a known top-cat but no resolved facet ids, so the
 * post-search facet aggregations contain the slug's option for the remap step.
 */
const browsePairsToFallbackKeyword = (pairs: BrowseDimensionPair[]): string =>
    pairs
        .map((pair) => pair.valueSlug.replace(/-/g, ' ').trim())
        .filter(Boolean)
        .join(' ')
        .trim();

/**
 * `search.facets` are global to the last search response; `runPreloadPhase` does not load facets for the incoming URL.
 * Reuse them for pre-search slug mapping only when the prior response was for the same L1 slug **and** pathname
 * (e.g. Warhol → Banksy under `/c/art/` must not map Banksy against Warhol aggregations).
 */
const searchFacetsApplyToBrowsePath = (
    stateAfterFacets: { browsePath?: { lastBrowseCategorySlug: string | null; lastBrowsePathname: string | null } },
    browseCategorySlug: string | undefined,
    pathname: string
): boolean =>
    Boolean(
        browseCategorySlug &&
        stateAfterFacets?.browsePath?.lastBrowseCategorySlug !== null &&
        stateAfterFacets?.browsePath?.lastBrowseCategorySlug === browseCategorySlug &&
        stateAfterFacets?.browsePath?.lastBrowsePathname === pathname
    );

export const resolveBrowseInputs = async ({
    deployment,
    locationSearch,
    routeContext,
    searchFacets,
    stateAfterFacets,
}: ResolveBrowseInputsArgs): Promise<BrowseResolutionResult> => {
    const {
        browseParsed,
        browseSplat,
        hasRegularLegacyCategory,
        isBrowseSlugRoute,
        isCombinationCategory,
        legacyCategoryId,
        legacyCategoryId2,
        legacyPathParsed,
        pathname,
    } = routeContext;

    const resolveBrowseUrlSafe = async (path: string): Promise<ResolveBrowseUrlApiResponse> => {
        try {
            return await fetchResolveBrowseUrl({
                deployment,
                pathname: path,
                search: '',
            });
        } catch {
            return { error: true };
        }
    };

    const redirectPayloadToResult = (
        redirectPathResolved: string
    ): Pick<
        BrowseResolutionResult,
        'baseCategoryIds' | 'browseFacetOptionIds' | 'browsePathPairsForPostSearchRemap' | 'redirectUrl'
    > => {
        const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
        const sanitizedRedirectPath = sanitizeBrowseRedirectPath(redirectPathResolved);
        return {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            redirectUrl: `${sanitizedRedirectPath}${querySuffix}`,
        };
    };

    let baseCategoryIds: number[] = [];
    let browseFacetOptionIds: Record<string, string[]> | undefined;
    let shouldAttemptPostSearchBrowseRemap = false;
    let browsePathPairsForPostSearchRemap: BrowseDimensionPair[] = [];
    /**
     * True only when the full-path `resolve-browse-url` call returned a non-empty `categoryIds`.
     * Slug-only resolve, the hardcoded top-cat fallback, and prior-state recovery all leave this
     * false — those sources resolve the *category* but not the URL's dimension/value, so the
     * loader still needs the post-search remap + keyword fallback to actually filter the results.
     */
    let baseCategoryIdsAreFromPathResolve = false;
    /** Backend-authoritative SEO outputs captured from full-path `resolve-browse-url` calls. */
    let browseSeo: BrowseSeoMeta | undefined;

    if (legacyPathParsed) {
        /**
         * Legacy ID-based URL (`/c/{slug}/{id}/`). category-api is authoritative: it 301s these to
         * the value form (`/c/{slug}/`) and provides canonical/robots/label. SSR handles the 301 in
         * the `redirectBrowseUrls` plugin; mirror it here for client-side navigation and capture the
         * SEO outputs either way.
         */
        const legacyResolved = await resolveBrowseUrlSafe(pathname);
        const legacyPayload = legacyResolved.error ? undefined : legacyResolved.payload;
        const legacyBaseCategoryIds = legacyPayload?.categoryIds?.length
            ? legacyPayload.categoryIds
            : [Number(legacyPathParsed.numericCategoryId)];
        const legacyRedirectPath = legacyPayload?.redirectPath;
        const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
        const shouldRedirect =
            typeof legacyRedirectPath === 'string' && legacyRedirectPath.length > 0 && legacyRedirectPath !== pathname;
        return {
            baseCategoryIds: legacyBaseCategoryIds,
            browseFacetOptionIds: legacyPayload?.facetOptionsByFacetId,
            browsePathPairsForPostSearchRemap: [],
            redirectUrl: shouldRedirect ? `${legacyRedirectPath}${querySuffix}` : null,
            seo: toSeoMeta(legacyPayload),
            shouldAttemptPostSearchBrowseRemap: false,
            unresolvedBrowseSlugForKeywordSearch: null,
            keywordFallbackForUnresolvedBrowse: null,
        };
    }

    const legacyFacetCanonicalPath = getLegacyBrowseCanonicalRedirectPath(pathname);
    if (legacyFacetCanonicalPath !== null) {
        const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
        return {
            baseCategoryIds: [],
            browseFacetOptionIds: undefined,
            browsePathPairsForPostSearchRemap: [],
            redirectUrl: `${legacyFacetCanonicalPath}${querySuffix}`,
            shouldAttemptPostSearchBrowseRemap: false,
            unresolvedBrowseSlugForKeywordSearch: null,
            keywordFallbackForUnresolvedBrowse: null,
        };
    }

    if (isBrowseSlugRoute && routeContext.params?.browseCategorySlug) {
        const slugOnlyPath = browseParsed
            ? buildBrowseCategoryPath(browseParsed.categorySlug, [], { base: browseParsed.base })
            : pathname.startsWith('/price-guide/')
              ? `/price-guide/${routeContext.params.browseCategorySlug}/`
              : pathname.startsWith('/similar-items/')
                ? `/similar-items/${routeContext.params.browseCategorySlug}/`
                : `/c/${routeContext.params.browseCategorySlug}/`;

        const pathPairsForBrowse = browseParsed?.pairs?.length > 0 ? browseParsed.pairs : parseBrowseTail(browseSplat);
        const hasMalformedBrowseSplat = Boolean(
            browseSplat && browseSplat.split('/').filter(Boolean).length > 0 && pathPairsForBrowse.length === 0
        );
        const rawFacetsForPathMapping = searchFacetsApplyToBrowsePath(
            stateAfterFacets,
            routeContext.params.browseCategorySlug,
            pathname
        )
            ? searchFacets
            : [];
        const canonicalPathPairs = sortBrowsePairsCanonical(pathPairsForBrowse);
        const pathMappedFacets = mapBrowsePairsToSearchOptionIds(canonicalPathPairs, rawFacetsForPathMapping);
        const pathMappedHasIds = hasAnyFacetIds(pathMappedFacets);

        if (browsePathHasTildeMultiValue(pathname)) {
            /**
             * Multi-value (`~`) combos are served but must be noindex with the canonical pointing at the
             * clean URL. The other branches skip `resolve-browse-url` for tilde paths, so resolve here
             * purely to capture the backend SEO directives (canonical/robots/indexable/label).
             */
            const tildeResolved = await resolveBrowseUrlSafe(pathname);
            if (!tildeResolved.error) {
                browseSeo = toSeoMeta(tildeResolved.payload) ?? browseSeo;
            }
        }

        const knownTopCategoryIdFromSlug = getTopCategoryIdFromSlug(routeContext.params.browseCategorySlug);
        if (knownTopCategoryIdFromSlug !== null) {
            baseCategoryIds = [knownTopCategoryIdFromSlug];
            if (hasMalformedBrowseSplat) {
                /**
                 * Indexed legacy URLs (`/c/{top}/{ambiguous}/`, price-guide/similar-items analogs) often mismatch
                 * strict browse dimensions. Try category-api `resolve-browse-url` before stripping to slug-only —
                 * use `redirectPath`, `categoryIds`, and `facetOptionsByFacetId` when present.
                 */
                const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
                const attemptMalformedResolver = !browsePathHasTildeMultiValue(pathname);
                if (!attemptMalformedResolver) {
                    return {
                        baseCategoryIds,
                        browseFacetOptionIds,
                        browsePathPairsForPostSearchRemap,
                        redirectUrl: `${slugOnlyPath}${querySuffix}`,
                        shouldAttemptPostSearchBrowseRemap,
                        unresolvedBrowseSlugForKeywordSearch: null,
                        keywordFallbackForUnresolvedBrowse: null,
                    };
                }
                const malformedResolved = await resolveBrowseUrlSafe(pathname);
                if (!malformedResolved.error) {
                    browseSeo = toSeoMeta(malformedResolved.payload) ?? browseSeo;
                }
                const malformedRedirectPath = malformedResolved.payload?.redirectPath;
                if (
                    !malformedResolved.error &&
                    typeof malformedRedirectPath === 'string' &&
                    malformedRedirectPath.length > 0 &&
                    malformedRedirectPath !== pathname &&
                    !isUnknownSlugOnlyBrowsePath(malformedRedirectPath) &&
                    !stripsLegacyNumericTailToOwnTopSlug(pathname, malformedRedirectPath)
                ) {
                    return {
                        ...redirectPayloadToResult(malformedRedirectPath),
                        shouldAttemptPostSearchBrowseRemap,
                        unresolvedBrowseSlugForKeywordSearch: null,
                        keywordFallbackForUnresolvedBrowse: null,
                    };
                }
                const malformedResolverClarified =
                    Boolean(malformedResolved.payload?.categoryIds?.length) ||
                    hasAnyFacetIds(malformedResolved.payload?.facetOptionsByFacetId);
                if (!malformedResolverClarified) {
                    /* Legacy numeric-tail URLs (e.g. `/c/art/lithograph/1/25811/`) often can't be auto-
                     * canonicalized and the resolver can't clarify them either. Fall through and render
                     * the top-cat instead of bouncing to slug-only, which loses meaningful context. */
                    if (!isLegacyFacetedNumericTailBrowsePath(pathname)) {
                        return {
                            baseCategoryIds,
                            browseFacetOptionIds,
                            browsePathPairsForPostSearchRemap,
                            redirectUrl: `${slugOnlyPath}${querySuffix}`,
                            shouldAttemptPostSearchBrowseRemap,
                            unresolvedBrowseSlugForKeywordSearch: null,
                            keywordFallbackForUnresolvedBrowse: null,
                        };
                    }
                }
                if (!malformedResolved.error && malformedResolved.payload?.categoryIds?.length) {
                    baseCategoryIds = malformedResolved.payload.categoryIds;
                    baseCategoryIdsAreFromPathResolve = true;
                }
                if (!malformedResolved.error && malformedResolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = malformedResolved.payload.facetOptionsByFacetId;
                }
            }

            if (pathPairsForBrowse.length > 0 && !pathMappedHasIds && !browsePathHasTildeMultiValue(pathname)) {
                const resolved = await resolveBrowseUrlSafe(pathname);
                if (!resolved.error) {
                    browseSeo = toSeoMeta(resolved.payload) ?? browseSeo;
                }
                const resolvedRedirectPath = resolved.payload?.redirectPath;
                if (
                    !resolved.error &&
                    typeof resolvedRedirectPath === 'string' &&
                    resolvedRedirectPath.length > 0 &&
                    resolvedRedirectPath !== pathname &&
                    !isUnknownSlugOnlyBrowsePath(resolvedRedirectPath) &&
                    !stripsLegacyNumericTailToOwnTopSlug(pathname, resolvedRedirectPath)
                ) {
                    return {
                        ...redirectPayloadToResult(resolvedRedirectPath),
                        shouldAttemptPostSearchBrowseRemap,
                        unresolvedBrowseSlugForKeywordSearch: null,
                        keywordFallbackForUnresolvedBrowse: null,
                    };
                }

                if (!resolved.error && resolved.payload?.categoryIds?.length) {
                    baseCategoryIds = resolved.payload.categoryIds;
                    baseCategoryIdsAreFromPathResolve = true;
                    if (!resolved.payload.facetOptionsByFacetId) {
                        const categoryIdBackedFallback = mapResolvedCategoryIdsToBrowseFacetOptions(
                            pathPairsForBrowse,
                            resolved.payload.categoryIds
                        );
                        if (categoryIdBackedFallback) {
                            browseFacetOptionIds = categoryIdBackedFallback;
                        }
                    }
                }

                if (!resolved.error && resolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = resolved.payload.facetOptionsByFacetId;
                }
            }
        } else {
            if (!browsePathHasTildeMultiValue(pathname)) {
                const resolved = await resolveBrowseUrlSafe(pathname);
                if (!resolved.error) {
                    browseSeo = toSeoMeta(resolved.payload) ?? browseSeo;
                }
                const resolvedRedirectUnknown = resolved.payload?.redirectPath;
                if (
                    !resolved.error &&
                    typeof resolvedRedirectUnknown === 'string' &&
                    resolvedRedirectUnknown.length > 0 &&
                    resolvedRedirectUnknown !== pathname &&
                    !isUnknownSlugOnlyBrowsePath(resolvedRedirectUnknown) &&
                    !stripsLegacyNumericTailToOwnTopSlug(pathname, resolvedRedirectUnknown)
                ) {
                    return {
                        ...redirectPayloadToResult(resolvedRedirectUnknown),
                        shouldAttemptPostSearchBrowseRemap,
                        unresolvedBrowseSlugForKeywordSearch: null,
                        keywordFallbackForUnresolvedBrowse: null,
                    };
                }
                if (!resolved.error && resolved.payload) {
                    if (resolved.payload.categoryIds?.length) {
                        baseCategoryIds = resolved.payload.categoryIds;
                        baseCategoryIdsAreFromPathResolve = true;
                    }
                    if (resolved.payload.facetOptionsByFacetId) {
                        browseFacetOptionIds = resolved.payload.facetOptionsByFacetId;
                    }
                }
            }

            if (!baseCategoryIds.length && slugOnlyPath !== pathname) {
                const slugResolved = await resolveBrowseUrlSafe(slugOnlyPath);
                if (!slugResolved.error && slugResolved.payload?.categoryIds?.length) {
                    baseCategoryIds = slugResolved.payload.categoryIds;
                }
                if (!browseFacetOptionIds && !slugResolved.error && slugResolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = slugResolved.payload.facetOptionsByFacetId;
                }
            }
        }

        if (!baseCategoryIds.length) {
            const topId = getTopCategoryIdFromSlug(routeContext.params.browseCategorySlug);
            if (topId) {
                baseCategoryIds = [topId];
            }
        }

        if (!baseCategoryIds.length && routeContext.params.browseCategorySlug) {
            const { baseCategoryIds: prevIds, lastBrowseCategorySlug } = stateAfterFacets.browsePath;
            if (
                prevIds.length > 0 &&
                lastBrowseCategorySlug !== null &&
                lastBrowseCategorySlug === routeContext.params.browseCategorySlug
            ) {
                baseCategoryIds = [...prevIds];
            }
        }

        const apiHasAnyFacetIds = hasAnyFacetIds(browseFacetOptionIds);
        if (!apiHasAnyFacetIds) {
            browseFacetOptionIds = pathMappedFacets;
        } else if (pathMappedHasIds) {
            browseFacetOptionIds = { ...browseFacetOptionIds, ...pathMappedFacets };
        }

        if (!hasAnyFacetIds(browseFacetOptionIds) && pathPairsForBrowse.length > 0) {
            shouldAttemptPostSearchBrowseRemap = true;
            browsePathPairsForPostSearchRemap = sortBrowsePairsCanonical(pathPairsForBrowse);
        }

        const unresolvedBrowseSlugForKeywordSearch =
            baseCategoryIds.length === 0 ? routeContext.params.browseCategorySlug : null;

        /**
         * Unresolved browse pairs: derive a keyword from the URL's slug values so the initial search
         * returns relevance-narrowed items + facet aggregations that contain the slug's option. The
         * remap step then promotes the heuristic match to an exact facet id and re-dispatches without
         * the keyword. If the remap still misses, the keyword-narrowed results remain visible — better
         * UX than rendering the unfiltered top-cat.
         *
         * Suppressed when the FULL-PATH resolver returned categoryIds: in that case the API itself
         * resolved the URL's dimension/value to a precise sub-cat (e.g. `/c/earrings/style/modern/`
         * → `[92]`) and adding a keyword would over-narrow by relevance. Slug-only resolve, top-cat
         * fallback, prior-state recovery, and unknown-L1 (no resolution at all) don't count — they
         * yield only the base category (or nothing), so the keyword is still needed to discover the
         * dimension/value option. When `baseCategoryIds` is empty (e.g. `/c/watches/...` where the
         * resolver returns nothing and `watches` isn't in the hardcoded top-cat list), the caller is
         * expected to combine this pair keyword with the URL slug for full coverage AND to recover
         * the L1 id from the post-search F1 facet for the categoryweb re-dispatch.
         */
        const keywordFallbackForUnresolvedBrowse =
            shouldAttemptPostSearchBrowseRemap && !baseCategoryIdsAreFromPathResolve
                ? browsePairsToFallbackKeyword(browsePathPairsForPostSearchRemap) || null
                : null;

        return {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            redirectUrl: null,
            seo: browseSeo,
            shouldAttemptPostSearchBrowseRemap,
            unresolvedBrowseSlugForKeywordSearch,
            keywordFallbackForUnresolvedBrowse,
        };
    }

    /**
     * `/c/:name/:name2/:id/:id/` once meant “facet slug + breadcrumb ids”. some indexed digit routes still match,
     * so we ask `resolve-browse-url` for a canonical `/c/.../{dimension}/{slug}/` redirect before concatenated combos.
     */
    if (!isBrowseSlugRoute && !legacyPathParsed && isCombinationCategory && !browsePathHasTildeMultiValue(pathname)) {
        const combinationResolved = await resolveBrowseUrlSafe(pathname);
        if (!combinationResolved.error) {
            browseSeo = toSeoMeta(combinationResolved.payload) ?? browseSeo;
        }
        const combinationRedirect = combinationResolved.payload?.redirectPath;
        if (
            !combinationResolved.error &&
            typeof combinationRedirect === 'string' &&
            combinationRedirect.length > 0 &&
            combinationRedirect !== pathname
        ) {
            return {
                ...redirectPayloadToResult(combinationRedirect),
                shouldAttemptPostSearchBrowseRemap,
                unresolvedBrowseSlugForKeywordSearch: null,
                keywordFallbackForUnresolvedBrowse: null,
            };
        }
    }

    if (isCombinationCategory) {
        /**
         * Legacy `/c/{topName}/{cat2Slug}/{topId}/{cat2Id}/` shapes: when `cat2Slug` is a non-numeric
         * label (creator/material/style/brand) and the resolver didn't redirect, we can't trust
         * `cat2Id` as a category filter — for creator URLs (e.g. `watches/patek-philippe/97/18874`)
         * `18874` is a creator facet id, not a category id, so passing it on `categories` returns
         * empty/wrong results and breaks the filter drawer's aggregations. Drop the second id,
         * keep the L1 (`topId`) as the category, narrow the initial search with the slug as a
         * keyword, and let the post-search remap promote the slug to its exact facet id across
         * any dimension (creator/material/origin/style/subject).
         */
        const cat2Slug = routeContext.params.categoryName2;
        const cat2IsSlug = typeof cat2Slug === 'string' && cat2Slug.length > 0 && !/^\d+$/.test(cat2Slug);
        if (cat2IsSlug && !Number.isNaN(legacyCategoryId)) {
            baseCategoryIds = [legacyCategoryId];
            browsePathPairsForPostSearchRemap = Object.keys(BROWSE_DIMENSION_PATH_TO_FACET_TYPE).map(
                (dimensionKey) => ({ dimensionKey, valueSlug: cat2Slug })
            );
            shouldAttemptPostSearchBrowseRemap = true;
            return {
                baseCategoryIds,
                browseFacetOptionIds,
                browsePathPairsForPostSearchRemap,
                redirectUrl: null,
                shouldAttemptPostSearchBrowseRemap,
                unresolvedBrowseSlugForKeywordSearch: null,
                keywordFallbackForUnresolvedBrowse: cat2Slug.replace(/-/g, ' ').trim() || null,
            };
        }
        baseCategoryIds = [legacyCategoryId, legacyCategoryId2].filter((id) => !Number.isNaN(id));
    } else if (hasRegularLegacyCategory) {
        baseCategoryIds = [legacyCategoryId];
    }

    return {
        baseCategoryIds,
        browseFacetOptionIds,
        browsePathPairsForPostSearchRemap,
        redirectUrl: null,
        seo: browseSeo,
        shouldAttemptPostSearchBrowseRemap,
        unresolvedBrowseSlugForKeywordSearch: null,
        keywordFallbackForUnresolvedBrowse: null,
    };
};
